import { getCart, updateCart } from './cart.js';
import { getCartUpdates } from './validator.js';
import {
  refreshCartUI,
  renderCartIconBubble,
  displayError,
  clearGiftRemovedMessage,
  GIFT_REMOVED_MESSAGE,
  CART_ICON_BUBBLE_SECTION,
} from './ui.js';

const CART_MUTATION_RE = /\/cart\/(add|change|update|clear)(\.js|\.json)?(\?|$)/;
const CART_CHANGE_RE = /\/cart\/change(\.js|\.json)?(\?|$)/;
const DEBOUNCE_MS = 300;

const _debug =
  typeof URLSearchParams !== 'undefined' &&
  typeof window !== 'undefined' &&
  new URLSearchParams(window.location?.search || '').has('debug_gwp_validator');

function log(...args: unknown[]) {
  if (_debug) console.debug('[GWP Validator]', ...args);
}

let _isValidating = false;
let _dirty = false;
let _noticeFresh = false;
let _debounceTimer: number | null = null;
// Set while an inline correction's own update.js round-trip is in flight. Gates the
// wrapper (step 1) so a concurrent mutation can't recurse or be re-processed mid-correction.
let _correcting = false;
// The unpatched window.fetch, captured at setup. The corrective POST uses it directly so it
// bypasses our wrapper entirely (no recursion, independent of _correcting).
let _originalFetch: typeof window.fetch | null = null;

function scheduleValidation(): void {
  clearTimeout(_debounceTimer as number);
  _debounceTimer = setTimeout(() => {
    _debounceTimer = null;
    void validateAndUpdate();
  }, DEBOUNCE_MS) as unknown as number;
}

async function validateAndUpdate(): Promise<void> {
  if (_isValidating) return;

  _isValidating = true;
  try {
    // Always refetch /cart.js — it carries the discount function's recomputed
    // final_line_price / line_level_discount_allocations, the authoritative state.
    const cart = await getCart();
    const updates = getCartUpdates(cart);

    if (Object.keys(updates).length > 0) {
      log('Updating ineligible/over-cap GWP lines:', updates);
      // Request the header bubble section: refreshCartUI() only makes Dawn re-render
      // cart line items, so the header count would otherwise stay stale.
      const updated = await updateCart(updates, [CART_ICON_BUBBLE_SECTION]);
      renderCartIconBubble(updated?.sections);
      // A gift was actually removed/trimmed — tell the customer why, BEFORE
      // refreshCartUI() publishes the cartUpdate that re-renders the drawer. The alert
      // node is a sibling of #CartDrawer, so the re-render leaves the message standing.
      displayError(GIFT_REMOVED_MESSAGE);
      _noticeFresh = true;
      refreshCartUI();
    } else {
      // The cart validates clean, so a standing gift-removed notice is now stale (the
      // customer re-qualified or removed the gift themselves). Retract exactly our
      // message; anything else on the node came from another producer and stays.
      if (_noticeFresh) _noticeFresh = false;
      else clearGiftRemovedMessage();
    }
  } catch (error) {
    // Critical path (cart fetch/update) failed — surface loudly even without the debug
    // flag. Detailed per-line tracing stays behind ?debug_gwp_validator.
    console.error('[GWP Validator] Validation failed:', error);
  } finally {
    _isValidating = false;
    // A mutation landed mid-validation (our own update.js, or a concurrent user
    // change). Re-run once so it isn't dropped; converges when nothing's left to do.
    if (_dirty) {
      _dirty = false;
      scheduleValidation();
    }
  }
}

// Extract the JSON request body of an intercepted fetch so we can reuse the caller's
// `sections` / `sections_url` in the corrective call. Dawn posts `fetch(url, { body })`
// with a string body (assets/cart.js:160); the Request-object branch is defensive.
async function readRequestBody(args: Parameters<typeof fetch>): Promise<string | null> {
  const opts = args[1];
  if (opts && typeof opts.body === 'string') return opts.body;
  const first = args[0];
  if (
    first &&
    typeof (first as Request).clone === 'function' &&
    typeof (first as Request).text === 'function'
  ) {
    try {
      return await (first as Request).clone().text();
    } catch {
      return null;
    }
  }
  return null;
}

// Inline-correct a /cart/change.js response *before* Dawn paints it. The change.js body is
// already the authoritative post-discount-function cart, so if it carries an over-cap /
// ineligible gift we remove it here and substitute the corrected (cart + sections) response.
// Dawn then performs a single, fully-correct paint — the bad state is never rendered. Any
// failure degrades to today's behavior: return the original response and let the debounced
// validator clean up. Never throws.
async function correctGwpInline(
  args: Parameters<typeof fetch>,
  response: Response,
): Promise<Response> {
  try {
    const cart = (await response.clone().json()) as Awaited<ReturnType<typeof getCart>> & {
      errors?: unknown;
    };

    // Error response (e.g. stock limit) — cart is unchanged; let Dawn render its error path.
    if (cart?.errors) return response;

    const updates = getCartUpdates(cart);
    if (Object.keys(updates).length === 0) {
      // Valid gift / nothing to cap. Keep the existing debounced behavior for safety.
      if (_isValidating) _dirty = true;
      else scheduleValidation();
      return response;
    }

    // Reuse the sections Dawn asked for so the corrected response matches its swap exactly.
    const reqBody = await readRequestBody(args);
    const parsed = reqBody
      ? (JSON.parse(reqBody) as { sections?: unknown; sections_url?: unknown })
      : null;
    const sections = parsed?.sections;
    if (!Array.isArray(sections) || sections.length === 0) {
      // Can't produce matching section HTML inline -> fall back to the debounced path.
      scheduleValidation();
      return response;
    }

    _correcting = true;
    const corrected = await _originalFetch!(window.Shopify!.routes.root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        updates,
        sections,
        sections_url: parsed!.sections_url,
      }),
    });
    if (!corrected.ok) throw new Error('corrective update.js failed: ' + corrected.status);

    log('Inline-corrected change.js:', updates);
    // A gift was actually removed/trimmed inline — tell the customer why. Dawn paints the
    // corrected sections next; the alert node sits outside every replaced root.
    displayError(GIFT_REMOVED_MESSAGE);
    // Cart is already correct -> do NOT scheduleValidation() (avoids a wasted debounced pass).
    return corrected;
  } catch (e) {
    console.error('[GWP Validator] Inline correction failed:', e);
    scheduleValidation(); // debounced cleanup
    return response; // Dawn paints normally (worst case = prior flash behavior)
  } finally {
    _correcting = false;
  }
}

function setupFetchInterceptor(): void {
  _originalFetch = window.fetch;
  window.fetch = async function (...args: Parameters<typeof fetch>): Promise<Response> {
    // Our own corrective call uses _originalFetch directly, so it never reaches here; this
    // guard catches *concurrent* third-party mutations landing mid-correction — pass them
    // straight through to avoid recursion, then validate their final cart state.
    if (_correcting) {
      const response = await _originalFetch!.apply(this, args);
      const url = typeof args[0] === 'string' ? args[0] : (args[0] as Request)?.url;
      if (url && CART_MUTATION_RE.test(url)) scheduleValidation();
      return response;
    }

    const response = await _originalFetch!.apply(this, args);

    const url = typeof args[0] === 'string' ? args[0] : (args[0] as Request)?.url;
    if (!url || !CART_MUTATION_RE.test(url)) return response;

    const method = (
      args[1]?.method ||
      (typeof args[0] === 'object' ? (args[0] as Request)?.method : null) ||
      'GET'
    ).toUpperCase();

    // The flash path: a successful POST /cart/change.js carries the post-function cart that
    // Dawn is about to paint synchronously. Detect + fix the gift line before that paint.
    if (method === 'POST' && CART_CHANGE_RE.test(url) && response.ok) {
      return correctGwpInline(args, response);
    }

    // Everything else (add/update/clear) keeps the debounced safety net. Mid-validation
    // mutations are coalesced into a single re-run via _dirty rather than scheduling — that's
    // what breaks the loop. Our own update.js always lands here, so a real update costs one
    // extra pass: a /cart.js GET + compute that finds nothing left to cap and stops. That
    // cost is intentional — it's the same mechanism that catches a concurrent *user* mutation
    // landing mid-validation, which would otherwise be dropped.
    if (_isValidating) _dirty = true;
    else scheduleValidation();
    return response;
  };
}

function init(): void {
  log('Initializing');
  setupFetchInterceptor();
  void validateAndUpdate();
}

// Guard against the script loading after DOMContentLoaded has already fired (deferred or
// late-injected bundle) — in that case the event never comes, so init() directly.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}

export {
  validateAndUpdate,
  setupFetchInterceptor,
  correctGwpInline,
  init,
  scheduleValidation,
  CART_MUTATION_RE,
  CART_CHANGE_RE,
  DEBOUNCE_MS,
};
